// Agent Communication Hub - Central message router
// frontend/src/agents/core/AgentCommunicationHub.ts

export interface Message {
    from: string;
    to?: string;
    type: 'question' | 'answer' | 'command' | 'event';
    payload: any;
    timestamp?: Date;
    correlationId?: string;
}

export interface MessageListener {
    handleMessage(message: Message): Promise<any>;
}

export class AgentCommunicationHub {
    private agents: Map<string, MessageListener> = new Map();
    private messageHistory: Message[] = [];
    private maxHistorySize: number = 1000;

    constructor() {
        console.log('[AgentCommunicationHub] Initialized');
    }

    /**
     * Register an agent with the hub
     */
    registerAgent(agentId: string, agent: MessageListener): void {
        this.agents.set(agentId, agent);
        console.log(`[AgentCommunicationHub] Registered agent: ${agentId}`);
    }

    /**
     * Unregister an agent
     */
    unregisterAgent(agentId: string): void {
        this.agents.delete(agentId);
        console.log(`[AgentCommunicationHub] Unregistered agent: ${agentId}`);
    }

    /**
     * Send a message to a specific agent
     */
    async sendMessage(message: Message): Promise<any> {
        // Add timestamp if not present
        if (!message.timestamp) {
            message.timestamp = new Date();
        }

        // Add correlation ID if not present (for tracking request/response)
        if (!message.correlationId && message.type === 'question') {
            message.correlationId = `${message.from}-${Date.now()}`;
        }

        // Log message
        this.logMessage(message);

        // Get target agent
        const targetAgent = this.agents.get(message.to);

        if (!targetAgent) {
            console.error(`[AgentCommunicationHub] Agent not found: ${message.to}`);
            throw new Error(`Agent not found: ${message.to}`);
        }

        // Route message to agent
        try {
            const result = await targetAgent.handleMessage(message);
            return result;
        } catch (error) {
            console.error(`[AgentCommunicationHub] Error routing message to ${message.to}:`, error);
            throw error;
        }
    }

    /**
     * Broadcast a message to all agents
     */
    broadcast(message: Message): void {
        // Add timestamp
        if (!message.timestamp) {
            message.timestamp = new Date();
        }

        message.to = 'broadcast';

        // Log message
        this.logMessage(message);

        // Send to all agents except sender
        for (const [agentId, agent] of this.agents.entries()) {
            if (agentId !== message.from) {
                agent.handleMessage({ ...message, to: agentId })
                    .catch(error => {
                        console.error(`[AgentCommunicationHub] Error broadcasting to ${agentId}:`, error);
                    });
            }
        }
    }

    /**
     * Log message to history
     */
    private logMessage(message: Message): void {
        this.messageHistory.push(message);

        // Trim history if too large
        if (this.messageHistory.length > this.maxHistorySize) {
            this.messageHistory = this.messageHistory.slice(-this.maxHistorySize);
        }

        console.log(`[Hub] ${message.from} → ${message.to} (${message.type})`);
    }

    /**
     * Get message history
     */
    getMessageHistory(filter?: {
        from?: string;
        to?: string;
        type?: string;
        since?: Date;
    }): Message[] {
        let history = this.messageHistory;

        if (filter) {
            if (filter.from) {
                history = history.filter(m => m.from === filter.from);
            }
            if (filter.to) {
                history = history.filter(m => m.to === filter.to);
            }
            if (filter.type) {
                history = history.filter(m => m.type === filter.type);
            }
            if (filter.since) {
                history = history.filter(m => m.timestamp >= filter.since!);
            }
        }

        return history;
    }

    /**
     * Get statistics
     */
    getStatistics(): {
        totalMessages: number;
        agentCount: number;
        messagesByType: Record<string, number>;
        messagesByAgent: Record<string, number>;
    } {
        const stats = {
            totalMessages: this.messageHistory.length,
            agentCount: this.agents.size,
            messagesByType: {} as Record<string, number>,
            messagesByAgent: {} as Record<string, number>
        };

        for (const message of this.messageHistory) {
            // Count by type
            stats.messagesByType[message.type] = (stats.messagesByType[message.type] || 0) + 1;

            // Count by agent (sender)
            stats.messagesByAgent[message.from] = (stats.messagesByAgent[message.from] || 0) + 1;
        }

        return stats;
    }

    /**
     * Clear message history
     */
    clearHistory(): void {
        this.messageHistory = [];
        console.log('[AgentCommunicationHub] Message history cleared');
    }

    /**
     * Get all registered agents
     */
    getRegisteredAgents(): string[] {
        return Array.from(this.agents.keys());
    }
}
